import { AxiosResponse } from 'axios';
import { getEnvironmentVariable } from '../helper/config';
import apiClient from '../utils/apiClient';
import axiosClient from '../utils/apiClient';
import { isApiError } from '../utils/helper';
import { EnvVariables, StorageKey } from '../utils/helper.type';
import {
  UsersResponse,
  CompleteOnboardingRequest,
  CompleteOnboardingResponse,
  LoginAsStaffResponseData,
  VerifyCodeResponse,
  VenueBranchLoginUserResponse,
  AuthorizedUsersResponse,
} from './service.type';
import { toast } from 'react-toastify';
import {
  CreateUserResponse,
  GetUsersResponse,
  UpdateUserResponse,
  UserType,
} from '@inplay/models';
import { EncryptionService } from '@inplay/services/dist/src/encryptionService';
import { AddOrganizationParams } from '../hooks/useUserData';
import { AddOrganizationUnitResponse } from '../pages/userManagement/venuesView/venuesView.type';
import { isUndefined } from 'lodash';

export class UserService {
  private baseApi: string;

  constructor() {
    const baseUrl: string | undefined = axiosClient.defaults.baseURL;

    if (!baseUrl) {
      throw new Error('baseURL is not defined in axiosClient.');
    }

    this.baseApi = baseUrl;
  }

  public async getUserSummary(): Promise<UsersResponse> {
    try {
      const usersSummaryApiResource = getEnvironmentVariable(
        EnvVariables.REACT_APP_USERS_RESOURCE,
      );

      const usersApiEndpoint = `${this.baseApi}${usersSummaryApiResource}`;

      const usersApiResponse: UsersResponse =
        await apiClient.get(usersApiEndpoint);

      console.log(`[usersService][getUserSummary]`, {
        usersApiResponse,
      });
      const usersSummary: UsersResponse = usersApiResponse;

      return usersSummary;
    } catch (error: unknown) {
      console.error(`[UserService][getUserSummary][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }

      throw error;
    }
  }

  public async resendInvitationEmail(email: string): Promise<boolean> {
    try {
      console.log(`[ENTRY][ResendInvitationEmailService]`);

      const resendApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_RESEND_EMAIL_RESOURCE,
      );
      const resendApiEndpoint: string = `${this.baseApi}${resendApiResource}`;
      const resendApiResponse: any = await apiClient.post(resendApiEndpoint, {
        // Todo from backend return message directly response.message so I puted as type any
        email,
      });
      console.log(`[ResendInvitationApiService]`, { resendApiResponse });
      toast.success(resendApiResponse?.message);
      return resendApiResponse.data;
    } catch (error: unknown) {
      console.error(`[ResendInvitationEmailService][Error]`, { error });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async getUserDetail(): Promise<GetUsersResponse> {
    try {
      console.log(`[ENTRY][UserService](getUserDetail)...`);

      const userDetailApiResource = getEnvironmentVariable(
        EnvVariables.REACT_APP_GET_USER_DETAIL_RESOURCE,
      );

      const userDetailApiEndpoint = `${this.baseApi}${userDetailApiResource}`;

      const params: Record<string, string> = {};
      const staffPin: string | null = localStorage.getItem(
        StorageKey.STAFF_PIN,
      );

      if (staffPin !== null) {
        params.pin = staffPin;
      }

      const userDetailApiResponse: AxiosResponse<GetUsersResponse> =
        await apiClient.get(userDetailApiEndpoint, {
          params,
        });

      console.log(`[usersService][getUserSummary]`, {
        userDetailApiResponse,
      });
      const userDetail: GetUsersResponse = userDetailApiResponse.data;
      console.log(`[EXIT][UserService](getUserDetail)`, { userDetail });
      return userDetail;
    } catch (error: unknown) {
      console.error(`[UserService][getUserSummary][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async activeDeactivateUser(
    ids: string | string[],
    setToActive: boolean,
  ): Promise<UpdateUserResponse> {
    try {
      console.log(`[ENTRY][UserService](activeDeactivateUser)...`, {
        ids,
        setToActive,
      });

      const activateDeactivateApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_ACTIVATE_DEACTIVATE_USER_RESOURCE,
      );

      const activateDeactivateApiEndpoint: string = `${this.baseApi}${activateDeactivateApiResource}`;

      const activateDeactivateApiResponse: UpdateUserResponse =
        await apiClient.put(activateDeactivateApiEndpoint, {
          ids,
          setToActive,
        });

      console.log(`[EXIT][UserService](activeDeactivateUser)`, {
        activateDeactivateApiResponse,
      });
      return activateDeactivateApiResponse;
    } catch (error: unknown) {
      console.error(`[UserService][activeDeactivateUser][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async completeOnboarding(
    data: CompleteOnboardingRequest,
  ): Promise<CompleteOnboardingResponse> {
    try {
      console.log(`[UserService][completeOnboarding][ENTRY]`, { data });

      const completeOnboardingResource = getEnvironmentVariable(
        EnvVariables.REACT_APP_COMPLETE_ONBOARDING_RESOURCE,
      );
      const completeOnboardingEndpoint = `${this.baseApi}${completeOnboardingResource}`;

      const response: CompleteOnboardingResponse = await apiClient.put(
        completeOnboardingEndpoint,
        data,
      );

      console.log(`[UserService][completeOnboarding]`, { response });
      toast.success(response.message);

      return response;
    } catch (error: unknown) {
      console.error(`[UserService][completeOnboarding][ERROR]`, { error });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async deleteUser(
    userIds: string | string[],
  ): Promise<UpdateUserResponse> {
    try {
      console.log(`[ENTRY][UserService](delete user)...`, {
        userIds,
      });

      const deleteUserApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_DELETE_USER_RESOURCE,
      );

      const deleteUserApiEndpoint: string = `${this.baseApi}${deleteUserApiResource}`;

      const ids: string | string[] = Array.isArray(userIds)
        ? userIds
        : [userIds];

      const params: URLSearchParams = new URLSearchParams();

      ids.forEach((id: string) => params.append('userIds', id));

      const urlWithParams: string = `${deleteUserApiEndpoint}?${params.toString()}`;

      const deleteApiResponse: UpdateUserResponse =
        await apiClient.delete(urlWithParams);

      console.log(`[EXIT][UserService](delete user)`, {
        deleteApiResponse,
      });
      return deleteApiResponse;
    } catch (error: unknown) {
      console.error(`[UserService][delete user][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async editVenue(
    organizationUnitId: string,
    name: string,
  ): Promise<UpdateUserResponse> {
    try {
      console.log(`[ENTRY][UserService](editVenue)...`, {
        organizationUnitId,
        name,
      });

      const editVenueApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_VENUE_UPDATE_RESOURCE,
      );

      const editVenueApiEndpoint: string = `${this.baseApi}${editVenueApiResource}`;

      const editVenueApiResponse: UpdateUserResponse = await apiClient.put(
        editVenueApiEndpoint,
        {
          organizationUnitId,
          name,
        },
      );

      console.log(`[EXIT][UserService](editVenue)`, {
        editVenueApiResponse,
      });
      return editVenueApiResponse;
    } catch (error: unknown) {
      console.error(`[UserService][editVenue][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async loginAsStaff(
    pin: string,
    venueId?: string,
  ): Promise<LoginAsStaffResponseData> {
    console.log(
      `[UserService][loginAsStaff] Before calling login as staff...`,
      {
        pin,
      },
    );

    try {
      const salt: string = await this.getVenueStaffSalt(venueId);
      const encryptionService = new EncryptionService();
      const hashedPin: string = encryptionService.hash(pin, salt);

      const loginAsStaffApiResource: string = getEnvironmentVariable(
        'REACT_APP_LOGIN_AS_STAFF',
      );

      const loginAsStaffApiEndpoint: string = `${this.baseApi}${loginAsStaffApiResource}`;
      const staffAccessToken = localStorage.getItem(
        StorageKey.STAFF_ACCESS_TOKEN,
      );

      const headers = staffAccessToken
        ? { Authorization: `Bearer ${staffAccessToken}` }
        : undefined;

      const loginAsStaffResponse: LoginAsStaffResponseData =
        await apiClient.post(
          loginAsStaffApiEndpoint,
          { pin: hashedPin },
          { headers },
        );

      console.log(
        `[UserService][loginAsStaff] Successfully logged in as staff`,
        {
          pin,
        },
      );

      return {
        ...loginAsStaffResponse,
        hashedPin,
      };
    } catch (error: unknown) {
      console.error(`[UserService][loginAsStaff][ERROR]`, { error });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    } finally {
      console.log(`[UserService][loginAsStaff] finally block`);
    }
  }

  public async getVenueStaffSalt(venueId?: string): Promise<string> {
    try {
      console.log(`[ENTRY][UserService](getVenueStaffSalt)...`, { venueId });
      const getVenueStaffSaltApiResource: string = getEnvironmentVariable(
        'REACT_APP_VENUE_STAFF_SALT',
      );

      const getVenueStaffSaltApiEndpoint: string = `${this.baseApi}${getVenueStaffSaltApiResource}`;
      const param: { venueId?: string | null } = {};
      if (venueId) {
        param.venueId = venueId;
      }
      const accessToken: string | null = localStorage.getItem(
        StorageKey.ACCESS_TOKEN,
      );
      const staffAccessToken: string | null = localStorage.getItem(
        StorageKey.STAFF_ACCESS_TOKEN,
      );
      const tokenToUse: string | null = accessToken || staffAccessToken;
      const headers = tokenToUse
        ? { Authorization: `Bearer ${tokenToUse}` }
        : undefined;
      const getVenueStaffSaltApiResponse: AxiosResponse<string> =
        await apiClient.get(getVenueStaffSaltApiEndpoint, {
          params: param,
          headers,
        });

      console.log(`[EXIT][UserService](getVenueStaffSalt)`, {
        getVenueStaffSaltApiResponse,
      });
      return getVenueStaffSaltApiResponse.data;
    } catch (error: unknown) {
      console.error(`[UserService][getVenueStaffSalt][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async createUser(
    firstName: string,
    lastName: string,
    email?: string,
    profilePhoto?: string,
    organizationId?: string,
    subOrganizationId?: string,
    subOrganizationIds?: string[],
    venueId?: string,
    venueBranchIds?: string[],
    venueBranchId?: string,
    mobileCountryCode?: string,
    mobileNumber?: string,
    pin?: string,
    pinOption?: string,
    type?: string,
    fetchUsersData?: () => Promise<void>,
  ): Promise<CreateUserResponse> {
    try {
      console.log(`[ENTRY][UserService](createUser)...`, {
        firstName,
        lastName,
        email,
        profilePhoto,
        organizationId,
        subOrganizationId,
        subOrganizationIds,
        venueId,
        venueBranchIds,
        venueBranchId,
        mobileCountryCode,
        mobileNumber,
        pin,
        pinOption,
        type,
        fetchUsersData,
      });

      let hashedPin: string | undefined;
      if (type === UserType.VENUE_STAFF) {
        const salt: string = await this.getVenueStaffSalt(venueId);
        const encryptionService = new EncryptionService();
        hashedPin = !isUndefined(pin)
          ? encryptionService.hash(pin, salt)
          : undefined;
      }

      const numberFormateMobileCountryCode: number | undefined =
        mobileCountryCode && !isNaN(Number(mobileCountryCode))
          ? Number(mobileCountryCode)
          : undefined;

      const numberFormateMobileNumber: number | undefined =
        mobileNumber && !isNaN(Number(mobileNumber))
          ? Number(mobileNumber)
          : undefined;
      const createUserApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_CREATE_USER_RESOURCE,
      );

      const createUserApiEndpoint: string = `${this.baseApi}${createUserApiResource}`;

      const basePayload: Record<string, any> = {
        firstName,
        lastName,
        type,
        ...(profilePhoto && { profilePhoto }),
      };

      let payload: Record<string, any> = { ...basePayload };

      switch (type) {
        case UserType.ORGANIZATION_ADMIN:
          payload = {
            ...basePayload,
            email,
            organizationId,
            ...(numberFormateMobileCountryCode !== undefined && {
              mobileCountryCode: numberFormateMobileCountryCode,
            }),
            ...(numberFormateMobileNumber !== undefined && {
              mobileNumber: numberFormateMobileNumber,
            }),
          };
          break;

        case UserType.SUB_ORGANIZATION_ADMIN:
          payload = {
            ...basePayload,
            email,
            subOrganizationId,
            ...(numberFormateMobileCountryCode !== undefined && {
              mobileCountryCode: numberFormateMobileCountryCode,
            }),
            ...(numberFormateMobileNumber !== undefined && {
              mobileNumber: numberFormateMobileNumber,
            }),
          };

          // Sub-organization ID can be an array when logged in user is a SubOrgAdmin
          // because the field is autopopulated with the userDetails.subOrganizationIds field which is always an array
          if (Array.isArray(subOrganizationId)) {
            payload.subOrganizationId = subOrganizationId[0];
          }
          break;

        case UserType.VENUE_ADMIN:
          payload = {
            ...basePayload,
            email,
            venueId,
            ...(numberFormateMobileCountryCode !== undefined && {
              mobileCountryCode: numberFormateMobileCountryCode,
            }),
            ...(numberFormateMobileNumber !== undefined && {
              mobileNumber: numberFormateMobileNumber,
            }),
          };
          break;

        case UserType.VENUE_MANAGER:
          payload = {
            ...basePayload,
            email,
            venueId,
            venueBranchIds,
            ...(numberFormateMobileCountryCode !== undefined && {
              mobileCountryCode: numberFormateMobileCountryCode,
            }),
            ...(numberFormateMobileNumber !== undefined && {
              mobileNumber: numberFormateMobileNumber,
            }),
          };
          break;
        case UserType.VENUE_STAFF:
          payload = {
            ...basePayload,
            venueId,
            venueBranchIds,
            ...(!isUndefined(hashedPin) && { pin: hashedPin }),
          };
          break;
      }
      const createUserApiResponse: CreateUserResponse = await apiClient.post(
        createUserApiEndpoint,
        payload,
      );

      console.log(`[EXIT][UserService](createUser)`, {
        createUserApiResponse,
      });
      return createUserApiResponse;
    } catch (error: unknown) {
      console.error(`[UserService][createUser][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async createUniversalStaffUser(
    email: string,
    venueId: string,
    venueBranchId: string,
    type: string,
  ): Promise<CreateUserResponse> {
    try {
      console.log(`[ENTRY][UserService](createUniversalStaffUser)...`, {
        email,
        venueId,
        venueBranchId,
        type,
      });

      const createUserApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_CREATE_USER_RESOURCE,
      );

      const createUserApiEndpoint: string = `${this.baseApi}${
        createUserApiResource.startsWith('/')
          ? createUserApiResource.slice(1)
          : createUserApiResource
      }`;

      const basePayload: Record<string, any> = {
        email,
        venueId,
        venueBranchId,
        type,
      };

      let payload: Record<string, any> = { ...basePayload };

      const createUserApiResponse: AxiosResponse<CreateUserResponse> =
        await apiClient.post(createUserApiEndpoint, payload);

      console.log(`[EXIT][UserService](createUniversalStaffUser)`, {
        createUserApiResponse,
      });
      return createUserApiResponse.data;
    } catch (error: unknown) {
      console.error(`[UserService][createUniversalStaffUser][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async updateUser(
    userId: string,
    firstName: string,
    lastName: string,
    email?: string,
    profilePhoto?: string,
    organizationId?: string,
    subOrganizationId?: string,
    subOrganizationIds?: string[],
    venueId?: string,
    venueBranchIds?: string[],
    venueBranchId?: string,
    mobileCountryCode?: string,
    mobileNumber?: string,
    pin?: string,
    type?: string,
    fetchUsersData?: () => Promise<void>,
  ): Promise<CreateUserResponse> {
    try {
      console.log(`[ENTRY][UserService](updateUser)...`, {
        userId,
        firstName,
        lastName,
        email,
        profilePhoto,
        organizationId,
        subOrganizationId,
        subOrganizationIds,
        venueId,
        venueBranchIds,
        venueBranchId,
        mobileCountryCode,
        mobileNumber,
        pin,
        type,
        fetchUsersData,
      });

      let hashedPin: string | undefined;

      if (type === UserType.VENUE_STAFF) {
        const salt: string = await this.getVenueStaffSalt(venueId);
        const encryptionService = new EncryptionService();
        hashedPin = !isUndefined(pin)
          ? encryptionService.hash(pin, salt)
          : undefined;
      }

      const numberFormateMobileCountryCode: number | undefined =
        mobileCountryCode && !isNaN(Number(mobileCountryCode))
          ? Number(mobileCountryCode)
          : undefined;

      const numberFormateMobileNumber: number | undefined =
        mobileNumber && !isNaN(Number(mobileNumber))
          ? Number(mobileNumber)
          : undefined;

      const createUserApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_UPDATE_USER_RESOURCE,
      );

      const updateUserApiEndpoint: string = `${this.baseApi}${createUserApiResource}`;

      const basePayload: Record<string, any> = {
        userId,
        firstName,
        lastName,
        type,
        profilePhoto,
      };

      let payload: Record<string, any> = { ...basePayload };

      switch (type) {
        case UserType.ORGANIZATION_ADMIN:
          payload = {
            ...basePayload,
            email,
            organizationId,
            ...(numberFormateMobileCountryCode !== undefined && {
              mobileCountryCode: numberFormateMobileCountryCode,
            }),
            ...(numberFormateMobileNumber !== undefined && {
              mobileNumber: numberFormateMobileNumber,
            }),
          };
          break;

        case UserType.SUB_ORGANIZATION_ADMIN:
          payload = {
            ...basePayload,
            email,
            subOrganizationId:
              subOrganizationId !== undefined ? subOrganizationId : '',
            ...(numberFormateMobileCountryCode !== undefined && {
              mobileCountryCode: numberFormateMobileCountryCode,
            }),
            ...(numberFormateMobileNumber !== undefined && {
              mobileNumber: numberFormateMobileNumber,
            }),
          };
          // Sub-organization ID will be an array because the field is autopopulated with the user.subOrganizationIds field which is always an array
          if (Array.isArray(subOrganizationId)) {
            payload.subOrganizationId = subOrganizationId[0];
          }
          break;

        case UserType.VENUE_ADMIN:
          payload = {
            ...basePayload,
            email,
            venueId,
            ...(numberFormateMobileCountryCode !== undefined && {
              mobileCountryCode: numberFormateMobileCountryCode,
            }),
            ...(numberFormateMobileNumber !== undefined && {
              mobileNumber: numberFormateMobileNumber,
            }),
          };
          break;

        case UserType.VENUE_MANAGER:
          payload = {
            ...basePayload,
            email,
            venueId,
            venueBranchIds,
            ...(numberFormateMobileCountryCode !== undefined && {
              mobileCountryCode: numberFormateMobileCountryCode,
            }),
            ...(numberFormateMobileNumber !== undefined && {
              mobileNumber: numberFormateMobileNumber,
            }),
          };
          break;

        case UserType.VENUE_STAFF:
          payload = {
            ...basePayload,
            venueId,
            venueBranchIds,
            ...(!isUndefined(hashedPin) ? { pin: hashedPin } : {}),
          };
          break;
      }

      const updateUserApiResponse: CreateUserResponse = await apiClient.put(
        updateUserApiEndpoint,
        payload,
      );

      console.log(`[EXIT][UserService](updateUser)`, {
        updateUserApiResponse,
      });

      return updateUserApiResponse;
    } catch (error: unknown) {
      console.error(`[UserService][updateUser][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async updateProfile(
    firstName?: string,
    lastName?: string,
    email?: string,
    signature?: string,
    mobileNumber?: number,
    profilePhoto?: string,
  ): Promise<UpdateUserResponse> {
    try {
      console.log(`[ENTRY][UserService](updateProfile)...`);
      const staffPin: string | null = localStorage.getItem(
        StorageKey.STAFF_PIN,
      );

      const payload: Record<string, unknown> = {
        firstName,
        lastName,
        email,
        signature,
        mobileNumber,
        profilePhoto,
      };

      if (staffPin) {
        payload.pin = staffPin;
      }

      const updateProfileApiEndpoint = `${this.baseApi}${getEnvironmentVariable(
        EnvVariables.REACT_APP_UPDATE_PROFILE_RESOURCE,
      )}`;

      const updateProfileApiResponse: UpdateUserResponse = await apiClient.put(
        updateProfileApiEndpoint,
        payload,
      );
      console.log(`[EXIT][UserService](updateProfile)`, {
        updateProfileApiResponse,
      });
      return updateProfileApiResponse;
    } catch (error) {
      console.error(`[UserService][updateProfile][ERROR]`, { error });
      if (isApiError(error)) throw error;
      throw error;
    }
  }

  public async verifyCode(
    email: string,
    verificationCode: string,
  ): Promise<VerifyCodeResponse> {
    try {
      const verifyCodeApiResource: string = getEnvironmentVariable(
        'REACT_APP_LOGIN_RESOURCE',
      );

      const verifyCodeApiEndpoint: string = `${this.baseApi}${verifyCodeApiResource}`;

      const verifyCodeApiResponse = await apiClient.post<VerifyCodeResponse>(
        verifyCodeApiEndpoint,
        {
          email,
          password: verificationCode,
        },
      );

      return verifyCodeApiResponse.data;
    } catch (error: unknown) {
      console.error(`[UserService][verifyCode][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async getVenueBranchLoginUser(
    venueBranchId: string,
  ): Promise<VenueBranchLoginUserResponse> {
    try {
      console.log(`[ENTRY][UserService](getVenueBranchLoginUser)...`, {
        venueBranchId,
      });

      const venueBranchLoginUserApiEndpoint = `${this.baseApi}users/get-venue-branch-login-user`;

      const response = await apiClient.get<VenueBranchLoginUserResponse>(
        venueBranchLoginUserApiEndpoint,
        {
          params: { venueBranchId },
        },
      );

      console.log(`[EXIT][UserService](getVenueBranchLoginUser)`, { response });
      return response.data;
    } catch (error: unknown) {
      console.error(`[UserService][getVenueBranchLoginUser][ERROR]`, { error });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async addOrganizationUnit(
    payload: AddOrganizationParams,
  ): Promise<AddOrganizationUnitResponse> {
    try {
      console.log(`[ENTRY][UserService](addOrganizationUnit)...`, {
        ...payload,
      });

      const addOrganizationApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_CREATE_ORGANIZATION_RESOURCE,
      );

      const addOrganizationApiEndpoint: string = `${this.baseApi}${addOrganizationApiResource}`;

      const addOrganizationApiResponse: AddOrganizationUnitResponse =
        await apiClient.post(addOrganizationApiEndpoint, {
          ...payload,
        });

      console.log(`[EXIT][UserService](addOrganizationUnit)`, {
        addOrganizationApiResponse,
      });
      return addOrganizationApiResponse;
    } catch (error: unknown) {
      console.error(`[UserService][addOrganizationUnit][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async getAuthUsers(
    organizationUnitId: string,
    scope: string,
  ): Promise<AuthorizedUsersResponse> {
    try {
      const authUsersApiResource = getEnvironmentVariable(
        EnvVariables.REACT_APP_AUTH_USERS_RESOURCE,
      );
      const authUsersApiEndpoint = `${this.baseApi}${authUsersApiResource}?scope=${scope || 'swd'}&organizationUnitId=${organizationUnitId}`;

      const authUsersApiResponse: AuthorizedUsersResponse =
        await apiClient.get(authUsersApiEndpoint);

      console.log(
        `[usersService][getAuthUsers]`,
        { organizationUnitId },
        {
          authUsersApiResponse,
        },
      );

      const authUsers: AuthorizedUsersResponse = authUsersApiResponse;

      return authUsers;
    } catch (error: unknown) {
      console.error(`[UserService][getAuthUsers][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async getUserSignature(userId: string): Promise<string> {
    try {
      const endpoint = `${this.baseApi}users/${userId}/signature`;
      const response: { data: { signature: string; userId: string } } =
        await apiClient.get(endpoint);
      return response?.data?.signature ?? '';
    } catch (error: unknown) {
      console.error(`[UserService][getUserSignature][ERROR]`, { error });
      return '';
    }
  }
}
